// src/App.js
// UPDATED (ecard-model drawing pivot):
//   The old parametric drawing pages have been REMOVED and replaced by
//   self-contained super-admin authoring pages:
//     - /super-admin/cabinet-drawings — author self-contained cabinet drawings
//     - /super-admin/item-drawings — author self-contained item drawings
//     - /super-admin/room-drawings — compose rooms from authored cabinets/items
//     - /super-admin/image-library — manage the shared image/material library
//       that feeds engine style assets.
//     - /super-admin/construction-profiles — edit which Image-Library type each
//       cabinet part pulls from, per construction (the engine's part->type map).
// FURTHER UPDATED (order workspace fork):
//   /order/:orderId now renders OrderWorkspaceRouter, which reads the order's
//   order_mode and mounts either OrderManagement (pricing) or, lazily,
//   DrawingOrderWorkspace (drawing). OrderManagement is still imported because
//   the router renders it directly. Super-admin drawing routes are unchanged.
//
// ============================================================================
// FURTHER UPDATED (admin dashboard)
// ============================================================================
//
// TWO CHANGES under /admin, and nothing else in this file moved.
//
// 1. AN INDEX ROUTE.
//
//    /admin had children but no element of its own. That worked only because
//    Admin.js redirected away from it the moment anyone arrived — so the path
//    was never actually rendered. Admin.js is now a DASHBOARD and that redirect
//    is gone, which means /admin has to resolve to something.
//
//    The index route renders nothing itself: Admin.js decides between the
//    dashboard and the <Outlet /> by looking at the path. The route exists so
//    React Router matches /admin as a complete destination rather than a bare
//    layout with no child.
//
// 2. CABINET FRAME TYPES IS A REDIRECT, NOT A DELETION.
//
//    Frame types folded into the Cabinet Types page — a cabinet type's frame
//    settings now sit with the cabinet type they belong to rather than on a
//    separate screen that made you remember which frame type went with which
//    construction.
//
//    The route stays as a redirect rather than being removed, because REMOVING
//    IT IS NOT NEUTRAL: the catch-all at the bottom of this file sends anything
//    unmatched to "/", so an open tab, a bookmark or a browser-history entry
//    would silently throw the user out to the marketing home page. A redirect
//    lands them on the page that absorbed it.
//
//    Safe to delete once nobody could plausibly still have the URL. Not soon.
//
//    The CabinetFrameTypes lazy import is gone with it. The page component
//    itself still exists on disk and can be deleted separately; leaving it does
//    no harm beyond the file sitting there.
//
// ============================================================================
// FURTHER UPDATED (doors split in two)
// ============================================================================
//
// /admin/outsourced-doors is new. Doors a shop builds and doors it buys
// finished are the same table separated by door.outsourcedboolean, and they now
// get a page each — the same shape drawer boxes already have.
//
// /admin/doors KEEPS ITS PATH and is unaffected. It is now scoped to in-house
// doors, but the URL, the component and the route are unchanged, so nothing
// bookmarked breaks and no redirect is needed.
//
// THE PATH LEADS WITH THE QUALIFIER — outsourced-doors, not doors-outsourced —
// and that is load-bearing rather than stylistic. adminNavItemForPath in
// adminNavigation.js falls back to longest-prefix matching, so a path of
// /admin/doors-outsourced would sit under the /admin/doors prefix and resolve
// to the wrong nav item: wrong heading, wrong fullBleed decision. The drawer
// box paths already follow this rule; any future split should too.
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/layout/Navbar.js';
import Footer from './components/layout/Footer.js';
import { useAuth } from './hooks/useAuth.js';
import { ThemeProvider } from './contexts/ThemeContext.js';
import ImpersonationBanner from './components/ui/ImpersonationBanner.js';
import LockedBanner from './components/ui/LockedBanner.js';

// Lazy-loaded components for better performance
const Home = lazy(() => import('./pages/Home.js'));
const Registration = lazy(() => import('./pages/auth/Registration.js'));
const Admin = lazy(() => import('./pages/Admin.js'));
const BrushingProcess = lazy(() => import('./pages/admin/BrushingProcess.js'));
const Employees = lazy(() => import('./pages/admin/Employees.js'));
const CabinetTypes = lazy(() => import('./pages/admin/CabinetTypes.js'));
const Labor = lazy(() => import('./pages/admin/Labor.js'));
const Material = lazy(() => import('./pages/admin/Material.js'));
const HourlyRates = lazy(() => import('./pages/admin/HourlyRates.js'));
// Doors built in this shop: boards, edgeband, panels, grains, edges, labour.
const Doors = lazy(() => import('./pages/admin/Doors.js'));
// Doors bought finished from a supplier: their square-foot rate, their fixed
// charges, and which materials each door comes in. Labour lives on this page
// too — a bought-in door is still received, sanded, drilled and hung.
const OutsourcedDoors = lazy(() => import('./pages/admin/OutsourcedDoors.js'));
const Drawerboxes = lazy(() => import('./pages/admin/Drawerboxes.js'));
const Finish = lazy(() => import('./pages/admin/Finish.js'));
const Hardware = lazy(() => import('./pages/admin/Hardware.js'));
const Items = lazy(() => import('./pages/admin/Items.js'));
const ItemEditPage = lazy(() => import('./pages/admin/ItemEditPage.js'));
const DrawerSlides = lazy(() => import('./pages/admin/DrawerSlides.js'));
// Legrabox / DTC and similar box-and-runner kits. Its own page because the
// rows behave nothing like a standalone runner — see adminNavigation.js.
const DrawerSystems = lazy(() => import('./pages/admin/DrawerSystems.js'));
// Boxes bought finished from a supplier and priced off their rate card.
const OutsourcedDrawerboxes = lazy(() => import('./pages/admin/OutsourcedDrawerboxes.js'));
const Settings = lazy(() => import('./pages/admin/Settings.js'));
const Freight = lazy(() => import('./pages/admin/Freight.js'));
const Installation = lazy(() => import('./pages/admin/Installation.js'));
const PricingModel = lazy(() => import('./pages/admin/PricingModel.js'));
const Orders = lazy(() => import('./pages/Orders.js'));
const Customers = lazy(() => import('./pages/admin/Customers.js'));
const Locations = lazy(() => import('./pages/admin/Locations.js'));
const StylePresets = lazy(() => import('./pages/admin/StylePresets.js'));
const OrderManagement = lazy(() => import('./pages/orders/OrderManagement.js'));
const OrderWorkspaceRouter = lazy(() => import('./pages/orders/OrderWorkspaceRouter.js'));
const Demo = lazy(() => import('./pages/demo/Demo.js'));
const SuperAdmin = lazy(() => import('./pages/SuperAdmin.js'));

// Stripe billing pages
const Pricing = lazy(() => import('./pages/Pricing.js'));
const Account = lazy(() => import('./pages/Account.js'));
const ChoosePlan = lazy(() => import('./pages/ChoosePlan.js'));
const Checkout = lazy(() => import('./pages/Checkout.js'));

// Public pages (Terms, Privacy, About)
const Terms = lazy(() => import('./pages/Terms.js'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy.js'));
const About = lazy(() => import('./pages/About.js'));
const InstructionManual = lazy(() => import('./pages/InstructionManual.js'));
// One plan family's demo chapter with a subscribe panel. Linked from the
// pricing cards' "See how it works". Public: the chapter endpoint decides
// what a signed-out visitor may read.
const HowItWorks = lazy(() => import('./pages/HowItWorks.js'));

// Super Admin management pages
const SuperAdminDashboard = lazy(() => import('./pages/superadmin/SuperAdminDashboard.js'));
const ManagePlans = lazy(() => import('./pages/superadmin/ManagePlans.js'));
const ManageCompanies = lazy(() => import('./pages/superadmin/ManageCompanies.js'));
const ManageMembers = lazy(() => import('./pages/superadmin/ManageMembers.js'));
const ManageAffiliates = lazy(() => import('./pages/superadmin/ManageAffiliates.js'));
const ManageSettings = lazy(() => import('./pages/superadmin/ManageSettings.js'));
const ManageMarketing = lazy(() => import('./pages/superadmin/ManageMarketing.js'));
const ManageEngines = lazy(() => import('./pages/superadmin/ManageEngines.js'));

// Super Admin self-contained drawing authoring pages (ecard model)
const ManageCabinetDrawings = lazy(() => import('./pages/superadmin/ManageCabinetDrawings.js'));
const ManageItemDrawings = lazy(() => import('./pages/superadmin/ManageItemDrawings.js'));
const ManageRoomDrawings = lazy(() => import('./pages/superadmin/ManageRoomDrawings.js'));
const MillworkAccessoriesPage = lazy(() => import('./pages/superadmin/MillworkAccessoriesPage.js'));
const ManageImageLibrary = lazy(() => import('./pages/superadmin/ManageImageLibrary.js'));
const ManageConstructionProfiles = lazy(() => import('./pages/superadmin/ManageConstructionProfiles.js'));
const ManageInstructionManual = lazy(() => import('./pages/superadmin/ManageInstructionManual.js'));
const AffiliateDashboard = lazy(() => import('./pages/affiliate/AffiliateDashboard.js'));
const AffiliateAgreement = lazy(() => import('./pages/affiliate/AffiliateAgreement.js'));

// Loading fallback component with styled loading bar
const LoadingFallback = () => (
  <div className="flex justify-center items-center h-64">
    <div className="bg-blue-50 dark:bg-slate-800 border border-blue-200 dark:border-slate-700 rounded-lg p-6 w-full max-w-md">
      <div className="flex flex-col items-center space-y-4">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-primary-400"></div>
          <span className="text-blue-700 dark:text-primary-300 font-medium">Loading...</span>
        </div>
        <div className="w-full bg-blue-100 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
          <div 
            className="h-full rounded-full"
            style={{
              background: 'linear-gradient(90deg, transparent, #3b82f6, transparent)',
              width: '50%',
              animation: 'loading-bar 1.5s infinite linear',
            }}
          />
        </div>
      </div>
    </div>
    <style>{`
      @keyframes loading-bar {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(300%); }
      }
    `}</style>
  </div>
);

function App() {
  const { isAuthenticated, loading, currentUser, isSuperAdmin, isImpersonating } = useAuth();
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const ProtectedRoute = ({ children }) => {
    if (loading) return <LoadingFallback />;
    if (!isAuthenticated) return <Navigate to="/" />;
    if (!currentUser) return <LoadingFallback />;
    return children;
  };

  const SuperAdminRoute = ({ children }) => {
    if (loading) return <LoadingFallback />;
    if (!isAuthenticated) return <Navigate to="/" />;
    if (!currentUser) return <LoadingFallback />;
    if (!isSuperAdmin() && !isImpersonating) {
      return <Navigate to="/" />;
    }
    return children;
  };

  const HomeRoute = () => {
    if (loading) return <LoadingFallback />;
    if (isAuthenticated && currentUser) {
      return <Navigate to="/orders" replace />;
    }
    return <Home />;
  };

  return (
    <ThemeProvider>
      <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-slate-900 transition-colors">
        {isImpersonating && <ImpersonationBanner />}

        {/* Subscription state.
            Mounted once here rather than per page: a locked customer should
            learn their account cannot save on whatever screen they happen to
            open, not only if they wander onto the billing tab.

            Renders nothing for active and free accounts, which is almost every
            session. It reads useEntitlement, which reads CompanyContext — so
            it must sit inside the provider tree, which it does.

            Above Navbar on purpose. A message that pushes the whole app down
            is noticed; one tucked below the nav is furniture. */}
        <LockedBanner />

        <Navbar />
        <main className="flex-grow relative">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomeRoute />} />
              <Route path="/register" element={<Registration />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/about" element={<About />} />
              {/* Public instruction manual. The API decides visibility (App Settings toggle; super admins always preview). */}
              <Route path="/instructions" element={<InstructionManual />} />
              <Route path="/how-it-works/:familyCode" element={<HowItWorks />} />

              {/* Super Admin Dashboard and Management Routes */}
              <Route path="/super-admin" element={<Navigate to="/super-admin/dashboard" replace />} />
              {/* AFFILIATE — ProtectedRoute, not SuperAdminRoute.
                  /affiliate is open to EVERY logged-in user by design. A cabinet
                  maker opens it once, reads what the program is, and never
                  returns; a consultant opens it and applies. The page decides
                  what to render from GET /api/affiliates/me, which answers for a
                  non-affiliate too.
                  There is no affiliate-only route guard and there should not be
                  one — gating the URL would mean nobody could find the page to
                  join from. */}
              <Route path="/affiliate" element={<ProtectedRoute><AffiliateDashboard /></ProtectedRoute>} />
              <Route path="/affiliate/agreement" element={<ProtectedRoute><AffiliateAgreement /></ProtectedRoute>} />

              <Route path="/super-admin/dashboard" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
              <Route path="/super-admin/plans" element={<SuperAdminRoute><ManagePlans /></SuperAdminRoute>} />
              <Route path="/super-admin/companies" element={<SuperAdminRoute><ManageCompanies /></SuperAdminRoute>} />
              <Route path="/super-admin/members" element={<SuperAdminRoute><ManageMembers /></SuperAdminRoute>} />
              {/* Affiliate program. Roster, per-affiliate detail, program
                  defaults and agreement versions all live on one page, so there
                  is a single route rather than a nested set. */}
              <Route path="/super-admin/affiliates" element={<SuperAdminRoute><ManageAffiliates /></SuperAdminRoute>} />
              <Route path="/super-admin/impersonation" element={<SuperAdminRoute><SuperAdmin /></SuperAdminRoute>} />
              <Route path="/super-admin/settings" element={<SuperAdminRoute><ManageSettings /></SuperAdminRoute>} />
              <Route path="/super-admin/marketing" element={<SuperAdminRoute><ManageMarketing /></SuperAdminRoute>} />

              {/* Super Admin drawing system (ecard / engine model) */}
              <Route path="/super-admin/cabinet-drawings" element={<SuperAdminRoute><ManageCabinetDrawings /></SuperAdminRoute>} />
              <Route path="/super-admin/item-drawings" element={<SuperAdminRoute><ManageItemDrawings /></SuperAdminRoute>} />
              <Route path="/super-admin/room-drawings" element={<SuperAdminRoute><ManageRoomDrawings /></SuperAdminRoute>} />
              <Route path="/super-admin/room-drawings/:roomId/millwork-accessories" element={<SuperAdminRoute><MillworkAccessoriesPage /></SuperAdminRoute>} />
              <Route path="/super-admin/image-library" element={<SuperAdminRoute><ManageImageLibrary /></SuperAdminRoute>} />
              <Route path="/super-admin/instruction-manual" element={<SuperAdminRoute><ManageInstructionManual /></SuperAdminRoute>} />
              <Route path="/super-admin/construction-profiles" element={<SuperAdminRoute><ManageConstructionProfiles /></SuperAdminRoute>} />
              <Route path="/super-admin/engines" element={<SuperAdminRoute><ManageEngines /></SuperAdminRoute>} />

              {/* Account & Billing Routes - Protected */}
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
              <Route path="/account/change-plan" element={<Navigate to="/pricing" replace />} />
              <Route path="/choose-plan" element={<ProtectedRoute><ChoosePlan /></ProtectedRoute>} />
              <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />

              {/* Orders Routes - Protected */}
              <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
              <Route path="/order/:orderId" element={<ProtectedRoute><OrderWorkspaceRouter /></ProtectedRoute>} />

              {/* Admin Routes - Protected */}
              <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>}>
                {/* THE DASHBOARD. Renders nothing of its own — Admin.js chooses
                    between the dashboard and its <Outlet /> by reading the path.
                    This route exists so React Router treats /admin as a complete
                    destination rather than a layout with no child, which is what
                    it was before the redirect was removed. */}
                <Route index element={null} />

                {/* REDIRECT, not a deletion. Frame types folded into Cabinet
                    Types; deleting this route would send an open tab or a
                    bookmark to the catch-all and out to the home page. */}
                <Route
                  path="cabinet-frame-types"
                  element={<Navigate to="/admin/cabinet-types" replace />}
                />

                <Route path="brushing-process" element={<BrushingProcess />} />
                <Route path="employees" element={<Employees />} />
                <Route path="cabinet-types" element={<CabinetTypes />} />
                <Route path="labor" element={<Labor />} />
                <Route path="materials" element={<Material />} />
                <Route path="hourly-rates" element={<HourlyRates />} />
                {/* Doors, split in two. Both gate on adminAccessDoors — see
                    adminNavigation.js on why that is one key and not two. */}
                <Route path="doors" element={<Doors />} />
                <Route path="outsourced-doors" element={<OutsourcedDoors />} />
                <Route path="drawerboxes" element={<Drawerboxes />} />
                <Route path="finish" element={<Finish />} />
                <Route path="hardware" element={<Hardware />} />
                {/* Style presets — the company-wide library of pricing recipes.
                    Authored here rather than only from inside an order; see the
                    page's own header. Sits next to hardware to match the order
                    adminNavigation renders. */}
                <Route path="style-presets" element={<StylePresets />} />
                <Route path="items" element={<Items />} />
                <Route path="items/:itemId" element={<ItemEditPage />} />
                <Route path="drawer-slides" element={<DrawerSlides />} />
                <Route path="drawer-systems" element={<DrawerSystems />} />
                <Route path="outsourced-drawerboxes" element={<OutsourcedDrawerboxes />} />
                <Route path="settings" element={<Settings />} />
                <Route path="freight" element={<Freight />} />
                <Route path="installation" element={<Installation />} />
                <Route path="pricing-model" element={<PricingModel />} />
                <Route path="customers" element={<Customers />} />
                <Route path="locations" element={<Locations />} />
              </Route>

              {/* Catch-all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default App;